import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  HelpCircle,
  Globe,
  ShieldAlert,
  DatabaseBackup,
  Boxes,
  ListChecks,
  Plug,
  ClipboardCheck,
} from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { cn } from '../../utils/cn';
import type { CheckCategory, CheckResult, CheckStatus } from '../../types';

const statusConfig: Record<CheckStatus, { icon: typeof CheckCircle2; className: string }> = {
  pass: { icon: CheckCircle2, className: 'text-pulse-500' },
  warn: { icon: AlertTriangle, className: 'text-amber-500' },
  fail: { icon: XCircle, className: 'text-rose-500' },
  unknown: { icon: HelpCircle, className: 'text-slate-300 dark:text-slate-600' },
};

const categoryConfig: Record<CheckCategory, { label: string; icon: typeof Globe }> = {
  connectivity: { label: 'Connectivity', icon: Plug },
  uptime: { label: 'Uptime & SSL', icon: Globe },
  security: { label: 'Security', icon: ShieldAlert },
  backups: { label: 'Backups', icon: DatabaseBackup },
  resources: { label: 'Resources', icon: Boxes },
  process: { label: 'Process health', icon: ListChecks },
  compliance: { label: 'Compliance', icon: ClipboardCheck },
};

function CheckRow({ result }: { result: CheckResult }) {
  const { icon: Icon, className } = statusConfig[result.status];
  const content = (
    <div className="flex items-start gap-3 py-2.5 -mx-1 px-1 rounded-lg transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40">
      <Icon size={16} className={cn('mt-0.5 shrink-0', className)} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{result.question}</p>
        <p className="text-xs text-slate-400">{result.detail}</p>
      </div>
    </div>
  );
  return result.href ? (
    <Link to={result.href}>{content}</Link>
  ) : (
    <div>{content}</div>
  );
}

export function ChecklistPanel({ results }: { results: CheckResult[] }) {
  if (results.length === 0) {
    return (
      <EmptyState
        icon={ListChecks}
        title="No checks configured yet"
        description="Checks activate automatically as you connect Jira, Wazuh, GitHub, Docker, or set up monitored sites and backup heartbeats."
      />
    );
  }

  const grouped = results.reduce<Record<string, CheckResult[]>>((acc, r) => {
    (acc[r.category] ??= []).push(r);
    return acc;
  }, {});

  const categoryOrder: CheckCategory[] = [
    'connectivity',
    'uptime',
    'security',
    'backups',
    'resources',
    'process',
    'compliance',
  ];

  return (
    <div className="space-y-5">
      {categoryOrder
        .filter((cat) => grouped[cat]?.length)
        .map((cat) => {
          const { label, icon: CatIcon } = categoryConfig[cat];
          return (
            <div key={cat}>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <CatIcon size={13} /> {label}
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {grouped[cat].map((r) => (
                  <CheckRow key={r.id} result={r} />
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
}
